import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import ReceiptOutlined from '@mui/icons-material/ReceiptOutlined';
import Box from '@mui/material/Box';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode } from 'react';

import { ProjectMaterialTotal, ProjectSummaryBasic } from '~hooks/useProjects/models';
import { Nullable } from '~types/Nullable';
import { toDecimal } from '~utils/decimal';

interface ProjectSummaryStripProps {
  materialTotals: ProjectMaterialTotal[];
  projectSummary: Nullable<ProjectSummaryBasic>;
}

const formatCount = (value: number) => value.toLocaleString();

const formatQuantity = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const labelSx = (theme: Theme) => ({
  alignItems: 'center',
  color: theme.brandV2.colors.treadGray4,
  display: 'flex',
  fontSize: '10px',
  fontWeight: 600,
  gap: 0.5,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
});

const valueSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadBlack,
  fontSize: '18px',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
  lineHeight: 1.15,
});

const iconSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadBlue,
  fontSize: '14px',
});

const mutedSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray4,
  fontSize: '12px',
  fontWeight: 500,
});

function Stat({
  icon,
  label,
  value,
}: {
  icon?: ReactNode;
  label: ReactNode;
  value: ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography sx={labelSx}>
        {icon}
        {label}
      </Typography>
      <Typography sx={valueSx}>{value}</Typography>
    </Box>
  );
}

export default function ProjectSummaryStrip({
  materialTotals,
  projectSummary,
}: ProjectSummaryStripProps) {
  if (!projectSummary) {
    return null;
  }

  const deliveredQuantity = projectSummary.totalQuantity;
  const deliveredUnitOfMeasure = projectSummary.totalQuantityUnitOfMeasure;

  const requestedQuantity = deliveredUnitOfMeasure
    ? materialTotals
        .filter((total) => total.unitOfMeasure?.id === deliveredUnitOfMeasure.id)
        .reduce((sum, total) => sum.plus(toDecimal(total.totalQuantity)), toDecimal(0))
        .toNumber()
    : 0;

  const hasRequestedTotal = requestedQuantity > 0;
  const deliveredPercent = hasRequestedTotal
    ? Math.min(100, Math.round((deliveredQuantity / requestedQuantity) * 100))
    : 0;

  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.brandV2.colors.treadGray9,
        borderTop: `1px solid ${theme.brandV2.colors.treadGray7}`,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.6fr)',
      })}
    >
      <Box sx={{ display: 'flex', gap: 3, px: 2, py: 1 }}>
        <Stat
          label={t('projects.summary.orders')}
          value={formatCount(projectSummary.ordersCount)}
        />
        <Stat
          icon={<CheckCircleOutlineOutlined sx={iconSx} />}
          label={t('projects.summary.completed_in_review')}
          value={formatCount(projectSummary.completedInReviewJobsCount)}
        />
        <Stat
          icon={<ReceiptOutlined sx={iconSx} />}
          label={t('projects.summary.tickets')}
          value={formatCount(projectSummary.totalTicketsCount)}
        />
      </Box>

      <Box
        sx={(theme) => ({
          borderLeft: `1px solid ${theme.brandV2.colors.treadGray7}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          px: 2,
          py: 1,
        })}
      >
        <Typography sx={labelSx}>{t('projects.summary.delivered')}</Typography>

        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <Typography component="span" sx={valueSx}>
            {formatQuantity(deliveredQuantity)}
            {hasRequestedTotal && (
              <Box component="span" sx={mutedSx}>
                {` / ${formatQuantity(requestedQuantity)}`}
              </Box>
            )}
            {deliveredUnitOfMeasure && (
              <Box
                component="span"
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadGray3,
                  fontSize: '12px',
                  fontWeight: 500,
                  ml: 0.5,
                })}
              >
                {deliveredUnitOfMeasure.name}
              </Box>
            )}
          </Typography>

          {hasRequestedTotal && (
            <>
              <LinearProgress
                value={deliveredPercent}
                variant="determinate"
                sx={(theme) => ({
                  borderRadius: 1,
                  flexGrow: 1,
                  height: '4px',
                  maxWidth: '200px',
                  minWidth: '48px',
                  [`&.${linearProgressClasses.root}`]: {
                    bgcolor: theme.brandV2.colors.treadGray7,
                  },
                  [`& .${linearProgressClasses.bar}`]: {
                    bgcolor: theme.brandV2.colors.treadBlue,
                  },
                })}
              />
              <Typography
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadGray3,
                  fontSize: '11px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                })}
              >
                {t('projects.summary.percent', { percent: deliveredPercent })}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
